import React, { useContext, useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Button,
  Box,
  useTheme,
  Grid,
  Tooltip,
  Select,
  MenuItem,
  SelectChangeEvent,
  MenuProps,
  TextField,
  Alert,
} from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import StarHalfIcon from "@mui/icons-material/StarHalf";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import { cartItemOptionType } from "../../interfaces/products/cartItemOption.interface";
import { getCartItem, saveInLocalStorage } from "../../config/localStorage";
import AppContext from "../../config/appContext";
import { getCurrencySymbol } from "../../constants/currency";

interface ProductCardProps {
  businessInfo: Record<string, string | number | boolean | any>;
  productId: string;
  categoryID: string;
  image: string;
  productName: string;
  description: string;
  price: number;
  discount: number;
  options: cartItemOptionType[];
  rating: number;
  totalQuantity: number;
  ribbonColor: string;
  buttonColor: string;
}

const ProductCard: React.FC<ProductCardProps> = ({
  businessInfo,
  productId,
  categoryID,
  image,
  productName,
  description,
  price,
  options,
  rating,
  totalQuantity,
  buttonColor,
}) => {
  const theme = useTheme();
  const { slug: businessSlug, paymentInfo } = businessInfo;

  const [selectedOption, setSelectedOption] = useState(options[0]);
  const [currentPrice, setCurrentPrice] = useState(options[0].price);
  const [currentCart, setCurrentCart] = useState<{ items: any[] }>({
    items: [],
  });
  const [cartItems, setCartItems] = useState({});
  const [quantity, setQuantity] = useState(0);
  const { cartUpdated, setCartUpdated } = useContext(AppContext);

  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const handleOptionChange = (event: SelectChangeEvent) => {
    const selectedOption = JSON.parse(event.target.value) as cartItemOptionType;
    setSelectedOption(selectedOption);
    setCurrentPrice(selectedOption.price);
    totalQuantity / selectedOption.unit >= 1 && setQuantity(0);
  };

  const handleQuantityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let inputValue = event.target.value;

    // If the input is empty, set quantity to 0
    if (inputValue === "") {
      setQuantity(0);
      return;
    }

    // Remove leading zeros by converting the string to a number
    const numericValue = Math.ceil(Number(inputValue));

    // Validate and set the quantity
    if (totalQuantity / selectedOption.unit >= numericValue) {
      setQuantity(numericValue);
    }
  };

  const handleAddToCart = () => {
    // Ensure quantity is greater than 0 before proceeding
    if (quantity <= 0) {
      return;
    }

    const tempItem = {
      productName: productName,
      productId: productId,
      productCategoryId: categoryID,
      optionName: selectedOption.name,
      optionId: selectedOption._id,
      optionSku: selectedOption.sku,
      price: selectedOption.price,
      productImg: image,
      qty: quantity,
      productDescription: selectedOption.description,
      productWeight: Number(selectedOption.weight),
      unit: selectedOption.unit,
    };

    console.log({ currentCart });

    // Create a copy of the current cart to avoid direct mutation
    const updatedCart = {
      ...currentCart,
      items: [...(currentCart.items || [])],
    };

    console.log({ updatedCart });

    // Check if the item already exists in the cart
    const existingItemIndex = updatedCart.items.findIndex(
      (item: any) =>
        item.optionId === selectedOption._id && item.productName === productName
    );

    console.log({ existingItemIndex });

    if (existingItemIndex !== -1) {
      // Update the quantity of the existing item
      updatedCart.items[existingItemIndex] = {
        ...updatedCart.items[existingItemIndex],
        qty: updatedCart.items[existingItemIndex].qty + quantity,
      };
    } else {
      // Add the new item if it doesn't exist
      updatedCart.items.push(tempItem);
    }

    console.log({ updatedCart });

    // Save the updated cart in local storage
    saveInLocalStorage(`cart-${businessSlug}`, updatedCart);

    // Update the state
    setCurrentCart(updatedCart);
    setCartUpdated(!cartUpdated);

    // Show the snackbar notification
    setSnackbarOpen(true);

    // Automatically close the notification after 1.5 seconds
    setTimeout(() => {
      setSnackbarOpen(false);
    }, 1500);
  };

  // const renderStars = () => {
  //   const stars = [];
  //   for (let i = 1; i <= 5; i++) {
  //     if (i <= rating) {
  //       stars.push(<StarIcon key={i} sx={{ color: "#FFD700" }} />);
  //     } else if (i - 0.5 === rating) {
  //       stars.push(<StarHalfIcon key={i} sx={{ color: "#FFD700" }} />);
  //     } else {
  //       stars.push(<StarBorderIcon key={i} sx={{ color: "#FFD700" }} />);
  //     }
  //   }
  //   return stars;
  // };

  const menuProps: Partial<MenuProps> = {
    PaperProps: {
      sx: {
        backgroundColor: theme.palette.primary.light,
        color: theme.palette.primary.main,
      },
    },
    MenuListProps: {
      sx: {
        "& .MuiMenuItem-root": {
          color: theme.palette.primary.main,
        },
      },
    },
  };

  useEffect(() => {
    const tempCart = getCartItem(`cart-${businessSlug}`);
    setCurrentCart(tempCart);
    if (tempCart) setCartItems(tempCart?.items);
  }, [cartUpdated]);

  return (
    <Card
      sx={{
        width: "100%",
        height: "100%",
        borderRadius: 3,
        overflow: "hidden",
        position: "relative",
        boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
        backgroundColor: theme.palette.primary.light,
        textAlign: "center",
      }}
    >
      {image ? (
        <CardMedia
          component="img"
          height="180"
          image={image}
          alt={productName}
        />
      ) : (
        <Box
          sx={{
            height: 180,
            backgroundColor: "transparent",
            border: "1px solid #ccc",
            borderRadius: "10 10 0 0",
          }}
        />
      )}
      <CardContent sx={{ padding: 1 }}>
        <Grid container>
          <Grid item container direction={"column"}>
            <Grid item container spacing={1}>
              <Grid item height={20} xs={7}>
                <Typography
                  fontSize={15}
                  fontWeight="bold"
                  textAlign={"left"}
                  color={"#948470"}
                >
                  {productName}
                </Typography>
              </Grid>
              <Grid item xs={5}>
                <Typography
                  fontSize={16}
                  fontWeight="bolder"
                  textAlign={"right"}
                  color={"#948470"}
                >
                  {currentPrice > 0 &&
                    `${getCurrencySymbol(
                      paymentInfo.currency
                    )} ${currentPrice.toFixed(2)}`}
                </Typography>
              </Grid>
            </Grid>
            <Grid item height={20} marginBottom={2}>
              <Typography
                variant="body2"
                sx={{ mt: 1 }}
                textAlign={"left"}
                color={"#948470"}
                fontSize={12}
              >
                {description.length > 35 ? (
                  <Tooltip title={description}>
                    <span>{description.substring(0, 35)}...</span>
                  </Tooltip>
                ) : (
                  description
                )}
              </Typography>
            </Grid>
          </Grid>

          <Grid item container alignItems="center" spacing={1} marginBottom={2}>
            <Grid item>
              <Select
                value={JSON.stringify(selectedOption)}
                onChange={handleOptionChange}
                displayEmpty
                MenuProps={menuProps}
                renderValue={(selected) => {
                  const selectedOption = JSON.parse(
                    selected as string
                  ) as cartItemOptionType;
                  return selectedOption.name;
                }}
                sx={{
                  width: "150px",
                  border: "1px solid #948470",
                  borderRadius: 1,
                }}
              >
                {options.map((option) => (
                  <MenuItem
                    key={option.sku}
                    value={JSON.stringify(option)}
                    sx={{ color: theme.palette.primary.main }}
                  >
                    {option.name}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item>
              <TextField
                type="number"
                value={quantity}
                onChange={handleQuantityChange}
                inputProps={{ min: 0 }}
                sx={{
                  width: "80px",
                  border: "1px solid #948470",
                  borderRadius: 1,
                  color: "#948470",
                  "& .MuiInputBase-input": {
                    textAlign: "center",
                  },
                }}
              />
            </Grid>
          </Grid>
        </Grid>
      </CardContent>
      <Box sx={{ position: "relative" }}>
        <Button
          fullWidth
          variant="contained"
          onClick={handleAddToCart}
          disabled={quantity < 1}
          sx={{
            mt: 2,
            bgcolor: buttonColor,
            color: "white",
            fontWeight: "bold",
            borderRadius: 0,
            margin: 0,
          }}
        >
          ADD TO CART
        </Button>

        {/* Notification Box */}
        {snackbarOpen && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1,
              borderRadius: "inherit",
            }}
          >
            <Alert
              severity="success"
              sx={{
                width: "80%",
                textAlign: "center",
                backgroundColor: theme.palette.success.main,
                color: "white",
              }}
            >
              Item added to cart!
            </Alert>
          </Box>
        )}
      </Box>
    </Card>
  );
};

export default ProductCard;
